import moment, { Moment } from 'moment';
import { memoize } from 'lodash';
import { nuiCtx } from './initialization';
import queryString from 'query-string';

export function dateIsToday(date) {
    return (
        moment()
            .startOf('date')
            .valueOf() ===
        moment(date)
            .startOf('date')
            .valueOf()
    );
}

export function getDayFromDate(date) {
    return moment(date).get('date');
}

export function isNull(obj) {
    return obj === null || typeof obj === 'undefined';
}

export function isJsonString(str) {
    try {
        JSON.parse(str);
    } catch (e) {
        return false;
    }
    return true;
}

export function isAfterToday(date: Moment) {
    return date.isAfter(moment(), 'day');
}

// super dolezity blocker BUG https://jira.nike.sk/browse/VVP-9932
export const jeZMinulehoStorocia = (date: Moment) => {
    return date.get('years') < 1900;
};

export function stripHtml(text) {
    return text
        ?.replace(/(<style[\w\W]+style>)/g, '')
        .replace(/(<([^>]+)>)/gi, '')
        .trim();
}

export function isUndefined(value) {
    return value === undefined;
}

export function integerFormat(n) {
    const numb = typeof n === 'string' ? parseFloat(n) : n;
    return formatNumber(numb.toFixed(0), 0);
}

export function currencyFormat(n, decPlaces = 2, roundDown = false) {
    return formatNumber(n, decPlaces, roundDown);
}

export function formatReward(type, value) {
    if (type === 'TEXT') {
        return value;
    }
    return value ? currencyFormat(value, 0) + ' ' + (type === 'NIK' ? '' : '€ ') + type : null;
}

export const formatDate = (date, format?) => {
    return date ? moment(date).format(format ? format : 'DD.MM.YYYY') : null;
};

export type Locale = 'sk' | 'en' | 'hu' | any;

export function getLocale(): Locale {
    return window.OPTIONS.locale || window.OPTIONS.lang;
}

export function userContractId(): number {
    return window?.OPTIONS?.account?.user?.user?.contractNumber || window?.USER_DATA_SNAPSHOT?.cisloZmluvy;
}

export function browserInfo() {
    let browserSupported = false;
    let browserGranted = false;
    let browserDenied = false;
    // browser capabilities
    if (window.Notification && Notification.requestPermission) {
        browserSupported = true;
        browserGranted = Notification.permission === 'granted';
        browserDenied = Notification.permission === 'denied';
    }
    return { browserSupported, browserGranted, browserDenied };
}

export const isMobileChannel = (): boolean => nuiCtx.platform === 'mobile';

export function getCurrency() {
    return isMobileChannel() ? window.OPTIONS.properties['actualCurrency'] : window.OPTIONS['currencySign'];
}

export function formatNumber(n, decPlaces = 2, roundDown = false) {
    if (isNaN(n) || n === null || typeof n === 'undefined') {
        return null;
    }

    // @ts-ignore
    const numf = typeof n === 'string' || n instanceof String ? parseFloat(n) : n;
    const sign = numf && numf < 0 ? '-' : '';
    const numb = Math.abs(numf);
    const fixedNumb = roundDown
        ? String((Math.floor(numb * Math.pow(10, decPlaces)) / Math.pow(10, decPlaces)).toFixed(decPlaces))
        : numb.toFixed(decPlaces);
    const decNumb = substringBefore(fixedNumb.toString(), '.');
    const formatted = decNumb.replace(/./g, (c, i, a) => (i && c !== '.' && (a.length - i) % 3 === 0 ? ' ' + c : c));

    const fraction = substringAfter(fixedNumb.toString(), '.');

    return fixedNumb.indexOf('.') === -1 ? formatted : sign + formatted + fraction;
}

export function formatMoneyAmount(n, currency?, decPlaces?) {
    return `${formatNumber(n, decPlaces)} ${currency || getCurrency()}`;
}

export function formatPointsAmount(n, decPlaces = 0) {
    return `${formatNumber(n, decPlaces)} NIK`;
}

function substringAfter(str, char) {
    return str.substring(str.indexOf(char));
}

function substringBefore(str, char) {
    if (str.indexOf(char) < 0) {
        return str;
    }
    return str.substring(0, str.indexOf(char));
}

export function formatStake(ticket) {
    if (ticket.get('pointsStake')) {
        return formatPointsAmount(ticket.get('pointsStakeAmount'));
    } else {
        return formatMoneyAmount(ticket.get('stake'));
    }
}

export function createNikeTicketShareURL(url: string, ticketHash: string, utm_source: string) {
    return createShareURL(url, {
        utm_content: ticketHash,
        utm_campaign: 'tiket',
        utm_medium: 'share-tiketu',
        utm_source: utm_source
    });
}

export function createNikeBetbookPostShareURL(url: string, postId, postType, postTitle, utm_source: string) {
    return createShareURL(url, {
        utm_content: `${postId}_${postTitle}`,
        utm_campaign: postType.toLowerCase(),
        utm_medium: 'betbook',
        utm_source: utm_source
    });
}

interface UTM_PARAMS {
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    utm_term?: string;
    utm_content?: string;
}

export function createShareURL(url: string, utm: UTM_PARAMS = {}) {
    return queryString.stringifyUrl({
        url,
        query: {
            utm_source: utm.utm_source?.slice(0, 100),
            utm_medium: utm.utm_medium?.slice(0, 100),
            utm_campaign: utm.utm_campaign?.slice(0, 100),
            utm_term: utm.utm_term?.slice(0, 100),
            utm_content: utm.utm_content?.slice(0, 100)
        }
    });
}

export const NOOP = () => {};
export const EMPTY_ARRAY = [];
export const EMPTY_OBJECT = {};

export const getSupportWebpFormat = memoize(function() {
    const elem = document.createElement('canvas');

    if (!!(elem.getContext && elem.getContext('2d'))) {
        return elem.toDataURL('image/webp').indexOf('data:image/webp') === 0;
    } else {
        return false;
    }
});

export const motionOpacityVariant = {
    initial: {
        opacity: 0
    },
    enter: {
        opacity: 1,
        transition: {
            duration: 0.3,
            ease: 'easeOut'
        }
    },
    exit: {
        opacity: 0,
        transition: {
            duration: 0.3,
            ease: 'easeIn'
        }
    }
};

export const motionModalSlideVariant = {
    initial: {
        bottom: -1000
    },
    enter: {
        bottom: 20,
        transition: {
            ease: 'easeOut',
            duration: 0.15
        }
    },
    exit: {
        transition: {
            ease: 'easeIn',
            duration: 0.15
        },
        bottom: -1000
    }
};

export const motionElementSlideVariant = {
    initial: {
        y: 50
    },
    enter: {
        y: 0,
        transition: {
            ease: 'easeOut',
            duration: 0.15
        }
    },
    exit: {
        transition: {
            ease: 'easeIn',
            duration: 0.15
        },
        y: 50
    }
};

export const toggleAndUpdateStorage = (db, id, path, key, isSession?: boolean) => {
    let newDb = toggleInDb(db, id, path);
    const storage = isSession ? sessionStorage : localStorage;
    storage.setItem(key, JSON.stringify(newDb.getIn([...path])?.toJS()));
    return newDb;
};

export const toggleInDb = (db, id, path) => {
    let newDb = db;

    if (newDb.getIn([...path, id])) {
        newDb = newDb.deleteIn([...path, id]);
    } else {
        newDb = newDb.setIn([...path, id], true);
    }
    return newDb;
};

export const toggleOneTournament = (db, id, path, key, sportName) => {
    let newDb = toggleInDb(db, id, [...path, sportName, 'customTournamentsIds']);
    sessionStorage.setItem(key, JSON.stringify(newDb.getIn([...path])?.toJS()));
    return newDb;
};

export const detectIsIE11 = () => {
    return !!(window as any).MSInputMethodContext && !!(document as any).documentMode;
};

export const detectIsEdge150OrOlder = () => {
    const ua = window.navigator.userAgent;

    const edge = ua.indexOf('Edge/');
    if (edge > 0) {
        const edgeHtmlVersion = parseInt(ua.substring(edge + 5, ua.indexOf('.', edge)), 10);
        return edgeHtmlVersion <= 15;
    }
};

export const formatToTractionDigits = (value: number, minimumFractionDigits = 0, maximumFractionDigits = 2) => {
    const formatter = new Intl.NumberFormat('sk-SK', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: minimumFractionDigits,
        maximumFractionDigits: maximumFractionDigits
    });

    return formatter
        .format(value)
        .toLocaleString()
        .replace(',', '.');
};

export const showGoalAnimation = (sportId: number) => [1, 3].includes(sportId);

export const getWindowOptionsProperty = <T = any>(property: string) => {
    return (window.OPTIONS.properties ? window.OPTIONS.properties[property] : window.OPTIONS[property]) as T;
};
